import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Activity, 
  CheckCircle2, 
  AlertTriangle, 
  ChevronDown, 
  ChevronUp,
  FileWarning,
  MapPin,
  Code,
  BookOpen
} from "lucide-react";

interface NewCountsDiagnostics {
  header: {
    found: boolean;
    cats: number | null;
    dogs: number | null;
    total: number | null;
    rawLine: string | null;
    lineIndex: number | null;
  };
  parsed: {
    cats: number;
    dogs: number;
    other: number;
    total: number;
  };
  matches: {
    cats: boolean;
    dogs: boolean;
    total: boolean;
    overall: boolean;
  };
  mismatches: Array<{
    species: 'CAT' | 'DOG' | 'TOTAL';
    expected: number;
    actual: number;
    difference: number;
  }>;
  effective?: {
    total: number;
  };
  totalsAccountedFor?: boolean | null;
}

interface LegacyCountsDiagnostics {
  total: number;
  bySpecies: Record<string, number>;
  byStatus: Record<string, number>;
}

type CountsDiagnostics = NewCountsDiagnostics | LegacyCountsDiagnostics;

function isNewFormat(counts: CountsDiagnostics): counts is NewCountsDiagnostics {
  return 'header' in counts;
}

interface DiscardedAnimalEntry {
  reason: 'missing_animal_id' | 'empty_buffer' | 'parse_error';
  pageIndex: number | null;
  zoneLabel: string | null;
  rawLinesSample: string[];
}

interface DiscardedAnimalsDiagnostics {
  count: number;
  reasons: DiscardedAnimalEntry[];
}

interface CapacityOverage {
  zone: string;
  count: number;
  capacity: number;
}

interface BreedLearningDiagnostics {
  new: number;
  updated: number;
}

export interface ReportDiagnostics {
  unknownZones?: string[];
  capacityOverages?: CapacityOverage[];
  counts?: CountsDiagnostics;
  discardedAnimals?: DiscardedAnimalsDiagnostics;
  breedLearning?: BreedLearningDiagnostics;
  inReportDedupCount?: number;
  unknownSpeciesAnimalIds?: string[];
}

interface ReportDiagnosticsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  diagnostics: ReportDiagnostics | null | undefined;
  location: string;
  reportDate: string;
}

function getReasonLabel(reason: string): string {
  const labels: Record<string, string> = {
    missing_animal_id: "Missing Animal ID",
    empty_buffer: "Empty Buffer",
    parse_error: "Parse Error",
  };
  return labels[reason] || reason;
}

export function ReportDiagnosticsDrawer({
  open,
  onOpenChange,
  diagnostics,
  location,
  reportDate,
}: ReportDiagnosticsDrawerProps) {
  const [showRawDiagnostics, setShowRawDiagnostics] = useState(false);
  const [showHeaderSource, setShowHeaderSource] = useState(false);
  const [expandedRawLines, setExpandedRawLines] = useState<Set<number>>(new Set());

  const counts = diagnostics?.counts;
  const discardedAnimals = diagnostics?.discardedAnimals;
  const unknownZones = diagnostics?.unknownZones;
  const capacityOverages = diagnostics?.capacityOverages;
  const breedLearning = diagnostics?.breedLearning;
  const inReportDedupCount = diagnostics?.inReportDedupCount;
  const unknownSpeciesAnimalIds = diagnostics?.unknownSpeciesAnimalIds;
  
  const hasNewFormat = counts && isNewFormat(counts);
  
  const discardedCountInfo = (() => {
    if (discardedAnimals?.count !== undefined) {
      return { count: discardedAnimals.count, inferred: false };
    }
    if (hasNewFormat && counts.effective?.total !== undefined && counts.parsed?.total !== undefined) {
      const inferred = counts.effective.total - counts.parsed.total;
      return { count: inferred >= 0 ? inferred : 0, inferred: true };
    }
    return { count: 0, inferred: false };
  })();
  
  const discardedCount = discardedCountInfo.count;
  const discardedIsInferred = discardedCountInfo.inferred;
  
  const effectiveTotal = hasNewFormat 
    ? (counts.effective?.total ?? counts.parsed.total + discardedCount)
    : 0;
  
  const isHealthy = hasNewFormat 
    ? counts.header.found === true &&
      (counts.totalsAccountedFor === true || 
        (counts.totalsAccountedFor === undefined && counts.matches?.overall === true)) &&
      discardedCount === 0
    : true;

  const hasZonesLayoutContent = 
    (unknownZones && unknownZones.length > 0) || 
    (capacityOverages && capacityOverages.length > 0);

  const toggleRawLines = (index: number) => {
    setExpandedRawLines(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="diagnostics-drawer">
        <SheetHeader className="mb-6">
          <SheetTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Report Diagnostics
          </SheetTitle>
          <SheetDescription>
            {location} — {reportDate}
          </SheetDescription>
        </SheetHeader>

        {!diagnostics ? (
          <div className="py-8 text-center" data-testid="diagnostics-unavailable">
            <FileWarning className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">
              Diagnostics not available for this report.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <Card data-testid="health-status-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Report Health
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isHealthy ? (
                  <Badge variant="default" className="gap-1" data-testid="badge-health-ok">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    OK
                  </Badge>
                ) : (
                  <Badge variant="outline" className="gap-1 border-amber-500 text-amber-600 dark:text-amber-400" data-testid="badge-health-warnings">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Warnings Present
                  </Badge>
                )}
              </CardContent>
            </Card>

            {!counts ? (
              <Card>
                <CardContent className="py-4">
                  <p className="text-sm text-muted-foreground">
                    Count diagnostics not available.
                  </p>
                </CardContent>
              </Card>
            ) : hasNewFormat ? (
              <>
                <Card data-testid="counts-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Animal Counts
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-muted-foreground">Header total</span>
                      <span className="font-medium text-right" data-testid="text-header-total">
                        {counts.header.found ? counts.header.total : "N/A"}
                      </span>

                      <span className="text-muted-foreground">Parsed animals</span>
                      <span className="font-medium text-right" data-testid="text-parsed-total">
                        {counts.parsed.total}
                      </span>

                      <span className="text-muted-foreground">Discarded animals</span>
                      <span className="font-medium text-right" data-testid="text-discarded-count">
                        {discardedCount}
                      </span>

                      <span className="text-muted-foreground">Effective total</span>
                      <span className="font-medium text-right" data-testid="text-effective-total">
                        {effectiveTotal}
                      </span>

                      {inReportDedupCount !== undefined && (
                        <>
                          <span className="text-muted-foreground">Duplicate entries collapsed</span>
                          <span className="font-medium text-right" data-testid="text-dedup-count">
                            {inReportDedupCount}
                          </span>
                        </>
                      )}
                    </div>

                    <p className="text-xs text-muted-foreground mt-2">
                      Effective total = parsed + discarded
                      {inReportDedupCount !== undefined && inReportDedupCount > 0 && (
                        <> · {inReportDedupCount} animal{inReportDedupCount !== 1 ? "s" : ""} appeared on multiple pages</>
                      )}
                    </p>

                    {!counts.header.found && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-2" data-testid="text-header-not-found">
                        Header counts not found in PDF — cannot compare to header totals.
                      </p>
                    )}

                    {counts.header.found && (counts.totalsAccountedFor === true || 
                      (counts.totalsAccountedFor === undefined && counts.matches?.overall === true && discardedCount === 0)) && (
                      <p className="text-xs text-green-600 dark:text-green-400 mt-2" data-testid="text-totals-accounted">
                        All header animals are accounted for.
                      </p>
                    )}

                    {counts.header.found && counts.totalsAccountedFor === false && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-2" data-testid="text-totals-mismatch">
                        Header total does not match effective total.
                      </p>
                    )}

                    {counts.header.found && counts.totalsAccountedFor === undefined && 
                      (counts.matches?.overall !== true || discardedCount > 0) && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-2" data-testid="text-totals-partial">
                        Count validation data incomplete. Re-upload PDF for full diagnostics.
                      </p>
                    )}

                    {counts.header.found && counts.header.rawLine && (
                      <div className="mt-3 pt-3 border-t">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-between text-muted-foreground p-0 h-auto hover:bg-transparent"
                          onClick={() => setShowHeaderSource(!showHeaderSource)}
                          data-testid="button-toggle-header-source"
                        >
                          <span className="flex items-center gap-1.5 text-xs">
                            <Code className="h-3 w-3" />
                            Header source
                          </span>
                          {showHeaderSource ? (
                            <ChevronUp className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5" />
                          )}
                        </Button>

                        {showHeaderSource && (
                          <div className="mt-2 space-y-1">
                            <p className="text-xs text-muted-foreground" data-testid="text-header-lineindex">
                              Line {(counts.header.lineIndex ?? 0) + 1}
                            </p>
                            <pre
                              className="text-xs bg-muted p-2 rounded overflow-x-auto whitespace-pre-wrap break-all"
                              data-testid="text-header-rawline"
                            >
                              <code>{counts.header.rawLine}</code>
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {counts.header.found && (
                  <Card data-testid="species-breakdown-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        Species Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Cats</span>
                          <div className="flex items-center gap-2">
                            <span>
                              {counts.header.cats ?? "?"} → {counts.parsed.cats}
                            </span>
                            {!counts.matches.cats && (
                              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Dogs</span>
                          <div className="flex items-center gap-2">
                            <span>
                              {counts.header.dogs ?? "?"} → {counts.parsed.dogs}
                            </span>
                            {!counts.matches.dogs && (
                              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                            )}
                          </div>
                        </div>
                        {counts.parsed.other > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Other</span>
                            <span>{counts.parsed.other}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {hasZonesLayoutContent && (
                  <Card data-testid="zones-layout-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                        <MapPin className="h-4 w-4" />
                        Kennels & Layout
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {unknownZones && unknownZones.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2">
                            Unknown Kennels ({unknownZones.length})
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {unknownZones.map((zone, index) => (
                              <Badge
                                key={index}
                                variant="outline"
                                className="border-amber-500 text-amber-600 dark:text-amber-400"
                                data-testid={`badge-unknown-zone-${index}`}
                              >
                                {zone}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {capacityOverages && capacityOverages.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2">
                            Capacity Overages ({capacityOverages.length})
                          </p>
                          <div className="space-y-1.5">
                            {capacityOverages.map((overage, index) => {
                              const overAmount = overage.count - overage.capacity;
                              return (
                                <div
                                  key={index}
                                  className="flex items-center gap-2 text-sm"
                                  data-testid={`overage-entry-${index}`}
                                >
                                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                                  <span className="font-medium">{overage.zone}:</span>
                                  <span>
                                    {overage.count} / {overage.capacity}
                                    {overAmount > 0 && (
                                      <span className="text-amber-600 dark:text-amber-400 ml-1">
                                        (+{overAmount} over)
                                      </span>
                                    )}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card data-testid="counts-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Animal Counts (Legacy Format)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-muted-foreground">Total animals</span>
                    <span className="font-medium text-right" data-testid="text-legacy-total">
                      {counts.total}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    This report uses an older diagnostics format. Re-upload the PDF to get detailed count validation.
                  </p>
                </CardContent>
              </Card>
            )}

            <Card data-testid="discarded-animals-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Discarded Animals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-medium mb-2" data-testid="text-discarded-summary">
                  {discardedCount} animal{discardedCount !== 1 ? "s" : ""} discarded
                  {discardedIsInferred && discardedCount > 0 && (
                    <span className="text-muted-foreground font-normal"> (inferred)</span>
                  )}
                </p>

                {discardedCount > 0 && discardedAnimals?.reasons && discardedAnimals.reasons.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {discardedAnimals.reasons.map((entry, index) => (
                      <div
                        key={index}
                        className="text-sm p-2 rounded bg-muted/50"
                        data-testid={`discarded-entry-${index}`}
                      >
                        <div>
                          <span className="font-medium">{getReasonLabel(entry.reason)}</span>
                          {entry.pageIndex !== null && (
                            <span className="text-muted-foreground"> — Page {entry.pageIndex + 1}</span>
                          )}
                          {entry.zoneLabel && (
                            <span className="text-muted-foreground"> — {entry.zoneLabel}</span>
                          )}
                        </div>

                        {entry.rawLinesSample && entry.rawLinesSample.length > 0 && (
                          <div className="mt-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-0 h-auto text-xs text-muted-foreground hover:bg-transparent"
                              onClick={() => toggleRawLines(index)}
                              data-testid={`button-toggle-rawlines-${index}`}
                            >
                              <span className="flex items-center gap-1">
                                <Code className="h-3 w-3" />
                                Raw lines
                                {expandedRawLines.has(index) ? (
                                  <ChevronUp className="h-3 w-3" />
                                ) : (
                                  <ChevronDown className="h-3 w-3" />
                                )}
                              </span>
                            </Button>

                            {expandedRawLines.has(index) && (
                              <pre
                                className="mt-2 text-xs bg-muted p-2 rounded overflow-x-auto"
                                data-testid={`rawlines-sample-${index}`}
                              >
                                <code>{entry.rawLinesSample.join('\n')}</code>
                              </pre>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {discardedCount > 0 && discardedIsInferred && (
                  <p className="text-xs text-muted-foreground mt-3">
                    Discarded count inferred from effective − parsed. Re-upload PDF for detailed breakdown.
                  </p>
                )}

                {discardedCount === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No animals were discarded during parsing.
                  </p>
                )}
              </CardContent>
            </Card>

            {unknownSpeciesAnimalIds && unknownSpeciesAnimalIds.length > 0 && (
              <Card data-testid="unknown-species-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4" />
                    Unknown Species
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm mb-3" data-testid="text-unknown-species-summary">
                    {unknownSpeciesAnimalIds.length} animal{unknownSpeciesAnimalIds.length !== 1 ? "s" : ""} with unresolved species
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {unknownSpeciesAnimalIds.map((id) => (
                      <Badge
                        key={id}
                        variant="outline"
                        className="border-amber-500 text-amber-600 dark:text-amber-400 font-mono text-xs"
                        data-testid={`badge-unknown-species-${id}`}
                      >
                        {id}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    These animals were accepted but may have reduced matching accuracy.
                  </p>
                </CardContent>
              </Card>
            )}

            {breedLearning !== undefined && (
              <Card data-testid="breed-learning-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                    <BookOpen className="h-4 w-4" />
                    Breed Learning
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-muted-foreground">New mappings</span>
                    <span className="font-medium text-right" data-testid="text-breed-new">
                      {breedLearning.new}
                    </span>
                    <span className="text-muted-foreground">Confirmed existing</span>
                    <span className="font-medium text-right" data-testid="text-breed-updated">
                      {breedLearning.updated}
                    </span>
                  </div>
                  {breedLearning.new === 0 && (
                    <p className="text-xs text-muted-foreground mt-3">
                      No new breed mappings learned from this report.
                    </p>
                  )}
                  {breedLearning.new > 0 && (
                    <p className="text-xs text-muted-foreground mt-3">
                      {breedLearning.new} breed{breedLearning.new !== 1 ? "s" : ""} added to the breed dictionary.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            <div className="pt-4 border-t">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-between text-muted-foreground"
                onClick={() => setShowRawDiagnostics(!showRawDiagnostics)}
                data-testid="button-toggle-raw"
              >
                <span>Technical details (DEV)</span>
                {showRawDiagnostics ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>

              {showRawDiagnostics && (
                <pre
                  className="mt-3 p-3 text-xs bg-muted rounded overflow-auto max-h-64"
                  data-testid="raw-diagnostics-json"
                >
                  {JSON.stringify(diagnostics, null, 2)}
                </pre>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
